import React, { useMemo, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { MonitorData } from '../types/client';
import { fmtNum } from '../utils/format';

// ── Constants ────────────────────────────────────────────────────────────────

const SECTION_LABELS: Record<string, string> = {
    screens:  'Aperturas de pantalla',
    groups:   'Grupos de endpoints',
    api_main: 'API',
};

const API_MAIN_SUFFIXES = ['rest', 'ina_api_public'];

function fmtSize(b: number): string {
    if (b >= 1e9) return `${(b / 1e9).toFixed(1)} GB`;
    if (b >= 1e6) return `${(b / 1e6).toFixed(1)} MB`;
    if (b >= 1e3) return `${(b / 1e3).toFixed(1)} KB`;
    return `${Math.round(b)} B`;
}

function pct(n: number, total: number): string {
    if (!total || n === 0) return '0%';
    return `${Math.round(n / total * 100)}%`;
}

// ── Types ────────────────────────────────────────────────────────────────────

type DayData = { date: string; requests: number; internal: number };

type AppRow = {
    router_suffix: string;
    app_name: string;
    category: string;
    totalRequests: number;
    totalErrors: number;
    avgMs: number;
    totalBandwidth: number;
    totalInternal: number;
    daily: DayData[];
};

type ScreenRow = {
    key: string;
    name: string;
    count: number;
    errors: number;
    avgMs: number;
    bandwidth: number;
    internal: number;
    daily: DayData[];
};

// ── SparkLine ────────────────────────────────────────────────────────────────

const W = 120, H = 26, PX = 2, PY = 3;

interface TipState { x: number; y: number; idx: number; }

const SparkLine: React.FC<{ data: DayData[] }> = ({ data }) => {
    const svgRef = useRef<SVGSVGElement>(null);
    const [tip, setTip] = useState<TipState | null>(null);
    const n = data.length;
    const maxV = Math.max(...data.map(d => d.requests), 1);

    const cx = (i: number) => PX + (n <= 1 ? (W - PX * 2) / 2 : (i / (n - 1)) * (W - PX * 2));
    const cy = (v: number) => PY + (H - PY * 2) * (1 - v / maxV);
    const pts = data.map((d, i) => `${cx(i)},${cy(d.requests)}`).join(' ');

    const handleMove = (e: React.MouseEvent<SVGSVGElement>) => {
        if (!svgRef.current || n === 0) return;
        const rect = svgRef.current.getBoundingClientRect();
        const relX = e.clientX - rect.left - PX;
        const idx = Math.max(0, Math.min(n - 1, Math.round((relX / (W - PX * 2)) * (n - 1))));
        setTip({ x: e.clientX, y: rect.top, idx });
    };

    const item = tip !== null ? data[tip.idx] : null;

    return (
        <div className="monitor-spark-wrap">
            <svg ref={svgRef} width={W} height={H} onMouseMove={handleMove} onMouseLeave={() => setTip(null)}>
                {n > 1 && (
                    <polyline points={pts} fill="none" stroke="var(--accent)" strokeWidth={1.5}
                        strokeLinejoin="round" strokeLinecap="round" opacity={0.75} />
                )}
                {tip !== null && item && (
                    <circle cx={cx(tip.idx)} cy={cy(item.requests)} r={3} fill="var(--accent)" />
                )}
            </svg>
            {tip !== null && item && createPortal(
                <div className="monitor-spark-tooltip-fixed" style={{ left: tip.x + 12, top: tip.y - 72 }}>
                    <div className="monitor-spark-tip-date">{item.date}</div>
                    <div>{fmtNum(item.requests)} peticiones</div>
                    {item.internal > 0 && <div className="monitor-spark-tip-int">{fmtNum(item.internal)} interno</div>}
                </div>,
                document.body
            )}
        </div>
    );
};

// ── Shared table ─────────────────────────────────────────────────────────────

type TableRow = {
    key: string;
    name: string;
    suffix?: string;
    requests: number;
    errors: number;
    avgMs: number;
    bandwidth: number;
    internal: number;
    daily: DayData[];
};

const errClass = (errors: number, requests: number): string => {
    if (!requests || errors === 0) return '';
    const p = errors / requests;
    return p > 0.05 ? 'monitor-val-red' : p > 0.01 ? 'monitor-val-amber' : '';
};

const AppTable: React.FC<{ rows: TableRow[]; maxRequests: number }> = ({ rows, maxRequests }) => (
    <div className="table-scroll-wrap">
        <table className="data-table monitor-app-table">
            <thead>
                <tr>
                    <th>Nombre</th>
                    <th className="monitor-col-spark">Uso por día</th>
                    <th className="col-num">Peticiones</th>
                    <th className="col-num">% Errores</th>
                    <th className="col-num">AVG ms</th>
                    <th className="col-num">Bandwidth</th>
                    <th className="col-num monitor-col-int">Interno</th>
                </tr>
            </thead>
            <tbody>
                {rows.map(row => {
                    const errPct = row.requests > 0 ? row.errors / row.requests : 0;
                    return (
                        <tr key={row.key}>
                            <td>
                                <span className="monitor-app-name">{row.name}</span>
                                {row.suffix && <span className="monitor-app-suffix">{row.suffix}</span>}
                            </td>
                            <td className="monitor-col-spark">
                                <SparkLine data={row.daily} />
                            </td>
                            <td className="col-num">{fmtNum(row.requests)}</td>
                            <td className={`col-num ${errClass(row.errors, row.requests)}`}>
                                {(errPct * 100).toFixed(1)}%
                            </td>
                            <td className="col-num">{Math.round(row.avgMs)} ms</td>
                            <td className="col-num">{fmtSize(row.bandwidth)}</td>
                            <td className="col-num monitor-col-int">
                                {row.internal > 0 ? fmtNum(row.internal) : '—'}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    </div>
);

// ── Main component ───────────────────────────────────────────────────────────

interface Props { data: MonitorData; }

const MonitorTab: React.FC<Props> = ({ data }) => {
    const [hoveredBar, setHoveredBar] = useState<number | null>(null);

    const snapshots = useMemo(
        () => [...data.snapshots].sort((a, b) => a.date.localeCompare(b.date)),
        [data]
    );

    const maxTotal = useMemo(() => Math.max(...snapshots.map(s => s.total), 1), [snapshots]);

    // ── Per-app aggregation ──────────────────────────────────────────────────
    const appsByCategory = useMemo(() => {
        const map: Record<string, {
            app_name: string; category: string;
            totalRequests: number; totalErrors: number; weightedMs: number;
            totalBandwidth: number; totalInternal: number;
            dailyMap: Record<string, { requests: number; internal: number }>;
        }> = {};

        for (const s of snapshots) {
            for (const app of (s.app_stats ?? [])) {
                if (!map[app.router_suffix]) {
                    map[app.router_suffix] = {
                        app_name: app.app_name, category: app.category || 'unknown',
                        totalRequests: 0, totalErrors: 0, weightedMs: 0,
                        totalBandwidth: 0, totalInternal: 0, dailyMap: {},
                    };
                }
                const e = map[app.router_suffix];
                e.totalRequests += app.client_requests;
                e.totalErrors += app.client_errors;
                e.weightedMs += app.client_avg_ms * app.client_requests;
                e.totalBandwidth += app.client_bandwidth_bytes;
                e.totalInternal += app.internal_requests;
                e.dailyMap[s.date] = { requests: app.client_requests, internal: app.internal_requests };
            }
        }

        const rows: AppRow[] = Object.entries(map).map(([suffix, d]) => ({
            router_suffix: suffix,
            app_name: d.app_name,
            category: d.category,
            totalRequests: d.totalRequests,
            totalErrors: d.totalErrors,
            avgMs: d.totalRequests > 0 ? d.weightedMs / d.totalRequests : 0,
            totalBandwidth: d.totalBandwidth,
            totalInternal: d.totalInternal,
            daily: snapshots.map(s => ({
                date: s.date,
                requests: d.dailyMap[s.date]?.requests ?? 0,
                internal: d.dailyMap[s.date]?.internal ?? 0,
            })),
        }));

        const grouped: Record<string, AppRow[]> = {};
        for (const row of rows) {
            if (!grouped[row.category]) grouped[row.category] = [];
            grouped[row.category].push(row);
        }
        for (const cat of Object.keys(grouped)) {
            grouped[cat].sort((a, b) => b.totalRequests - a.totalRequests);
        }
        return grouped;
    }, [snapshots]);

    // ── Per-screen aggregation ───────────────────────────────────────────────
    const screenRows = useMemo((): ScreenRow[] => {
        const map: Record<string, {
            name: string; count: number; errors: number; weightedMs: number;
            bandwidth: number; internal: number;
            dailyMap: Record<string, { requests: number; internal: number }>;
        }> = {};

        for (const s of snapshots) {
            for (const sc of (s.screen_opens ?? [])) {
                if (!map[sc.key]) {
                    map[sc.key] = {
                        name: sc.name, count: 0, errors: 0, weightedMs: 0,
                        bandwidth: 0, internal: 0, dailyMap: {},
                    };
                }
                const e = map[sc.key];
                e.count += sc.count;
                e.errors += sc.errors;
                e.weightedMs += sc.avg_ms * sc.count;
                e.bandwidth += sc.bandwidth_bytes;
                e.internal += sc.internal_count;
                e.dailyMap[s.date] = { requests: sc.count, internal: sc.internal_count };
            }
        }

        return Object.entries(map)
            .map(([key, d]) => ({
                key,
                name: d.name,
                count: d.count,
                errors: d.errors,
                avgMs: d.count > 0 ? d.weightedMs / d.count : 0,
                bandwidth: d.bandwidth,
                internal: d.internal,
                daily: snapshots.map(s => ({
                    date: s.date,
                    requests: d.dailyMap[s.date]?.requests ?? 0,
                    internal: d.dailyMap[s.date]?.internal ?? 0,
                })),
            }))
            .sort((a, b) => b.count - a.count);
    }, [snapshots]);

    // ── Per-endpoint-group aggregation ──────────────────────────────────────
    const groupTableRows = useMemo((): TableRow[] => {
        const map: Record<string, {
            count: number; errors: number; weightedMs: number;
            bandwidth: number; internal: number;
            dailyMap: Record<string, { requests: number; internal: number }>;
        }> = {};

        for (const s of snapshots) {
            for (const g of (s.endpoint_groups ?? [])) {
                if (!map[g.name]) {
                    map[g.name] = { count: 0, errors: 0, weightedMs: 0, bandwidth: 0, internal: 0, dailyMap: {} };
                }
                const e = map[g.name];
                e.count += g.requests;
                e.errors += g.client_errors + g.server_errors;
                e.weightedMs += g.avg_ms * g.requests;
                e.bandwidth += g.bandwidth_bytes;
                e.internal += g.internal_requests;
                e.dailyMap[s.date] = { requests: g.requests, internal: g.internal_requests };
            }
        }

        return Object.entries(map)
            .map(([name, d]) => ({
                key: name,
                name,
                requests: d.count,
                errors: d.errors,
                avgMs: d.count > 0 ? d.weightedMs / d.count : 0,
                bandwidth: d.bandwidth,
                internal: d.internal,
                daily: snapshots.map(s => ({
                    date: s.date,
                    requests: d.dailyMap[s.date]?.requests ?? 0,
                    internal: d.dailyMap[s.date]?.internal ?? 0,
                })),
            }))
            .sort((a, b) => b.requests - a.requests);
    }, [snapshots]);

    // Convert ScreenRow → TableRow
    const screenTableRows: TableRow[] = screenRows.map(r => ({
        key: r.key,
        name: r.name,
        requests: r.count,
        errors: r.errors,
        avgMs: r.avgMs,
        bandwidth: r.bandwidth,
        internal: r.internal,
        daily: r.daily,
    }));

    const maxRequests = useMemo(() => {
        const allRows = [
            ...screenTableRows,
            ...Object.values(appsByCategory).flat().map(r => ({ requests: r.totalRequests })),
        ];
        return Math.max(...allRows.map(r => r.requests), 1);
    }, [screenTableRows, appsByCategory]);

    const hovered = hoveredBar !== null ? snapshots[hoveredBar] : null;

    // Build sections to render
    const apiMainRows: TableRow[] = API_MAIN_SUFFIXES
        .map(suffix => {
            const row = Object.values(appsByCategory).flat().find(r => r.router_suffix === suffix);
            if (!row) return null;
            return {
                key: row.router_suffix,
                name: row.app_name,
                requests: row.totalRequests,
                errors: row.totalErrors,
                avgMs: row.avgMs,
                bandwidth: row.totalBandwidth,
                internal: row.totalInternal,
                daily: row.daily,
            } as TableRow;
        })
        .filter((r): r is TableRow => r !== null);

    const sections: Array<{ id: string; rows: TableRow[] }> = [];
    if (screenTableRows.length) sections.push({ id: 'screens', rows: screenTableRows });
    if (groupTableRows.length) sections.push({ id: 'groups', rows: groupTableRows });
    if (apiMainRows.length) sections.push({ id: 'api_main', rows: apiMainRows });

    return (
        <div className="monitor-tab-root">
            {/* ── Gráfica de barras diaria ── */}
            <div className="section-title">Actividad diaria — últimos {data.days} días</div>

            <div className="monitor-chart-wrap">
                <div className="monitor-chart-bars">
                    {snapshots.map((s, i) => {
                        const barH = Math.round((s.total / maxTotal) * 100);
                        const errH = s.total > 0
                            ? Math.round(((s.client_errors + s.server_errors) / s.total) * 100)
                            : 0;
                        return (
                            <div
                                key={s.date}
                                className={`monitor-bar-col${hoveredBar === i ? ' hovered' : ''}`}
                                onMouseEnter={() => setHoveredBar(i)}
                                onMouseLeave={() => setHoveredBar(null)}
                            >
                                <div className="monitor-bar-track">
                                    <div className="monitor-bar" style={{ height: `${barH}%` }}>
                                        {errH > 0 && <div className="monitor-bar-err" style={{ height: `${errH}%` }} />}
                                    </div>
                                </div>
                                <div className="monitor-bar-label">{s.date.slice(8)}</div>
                            </div>
                        );
                    })}
                </div>

                <div className="monitor-chart-detail">
                    {hovered ? (
                        <div className="monitor-chart-detail-inner">
                            <span className="monitor-detail-date">{hovered.date}</span>
                            <span><span className="monitor-detail-label">Total</span>{fmtNum(hovered.total)}</span>
                            <span><span className="monitor-detail-label">OK</span>{pct(hovered.success, hovered.total)}</span>
                            <span className={errClass(hovered.client_errors, hovered.total)}>
                                <span className="monitor-detail-label">4xx</span>{pct(hovered.client_errors, hovered.total)}
                            </span>
                            <span className={errClass(hovered.server_errors, hovered.total)}>
                                <span className="monitor-detail-label">5xx</span>{pct(hovered.server_errors, hovered.total)}
                            </span>
                            <span><span className="monitor-detail-label">Avg</span>{Math.round(hovered.avg_ms)} ms</span>
                            <span><span className="monitor-detail-label">P95</span>{Math.round(hovered.p95_ms)} ms</span>
                            <span><span className="monitor-detail-label">IPs</span>{hovered.unique_ips}</span>
                        </div>
                    ) : (
                        <span className="monitor-chart-hint">Pasa el cursor sobre una barra para ver el detalle</span>
                    )}
                </div>
            </div>

            {/* ── Secciones ── */}
            {sections.map(({ id, rows }) => (
                <div key={id} className="monitor-section">
                    <div className="section-title">
                        {SECTION_LABELS[id]}
                        <span className="monitor-cat-badge">{rows.length}</span>
                    </div>
                    <AppTable rows={rows} maxRequests={maxRequests} />
                </div>
            ))}
        </div>
    );
};

export default MonitorTab;
